import { company, companyLocation, printIdentifiers } from './reference-data.js';

/**
 * Printing a document, rather than printing the screen.
 *
 * `printVoucher()` in `VoucherEntry.tsx` established the shape and the reason
 * for it: a screen is a register, an entry panel, a validation card and a stat
 * strip, and none of that belongs in a filing cabinet. What does is the
 * document — who the company is, which document this is, what is on it, what it
 * comes to, and two signatures. So the document is composed as its own HTML and
 * printed from a hidden iframe.
 *
 * An iframe rather than `window.open`, because a popup blocker silently eating a
 * print is indistinguishable from a printer that is off.
 *
 * This module is that machinery pulled out of the voucher screen, because five
 * more documents needed it — the consignment, the purchase invoice, the sales
 * invoice and the two returns — and a sixth copy of the letterhead is a sixth
 * place the company address can be wrong. The voucher keeps its own composer:
 * its body is a debit/credit table with a balance to assert, which is not the
 * shape of a trade document, and forcing both through one template would make
 * the template the union of two documents rather than either.
 */

/** Text into HTML. Everything below interpolates user-entered narration. */
export function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface PrintColumn {
  label: string;
  align?: 'left' | 'right';
  width?: string;
}

/** One of the labelled facts across the head of a document. */
export interface PrintFact {
  label: string;
  value: string;
}

/** A `label: value` line in the totals block, `strong` for the grand total. */
export interface PrintTotal {
  label: string;
  value: string;
  strong?: boolean;
}

export interface PrintDocument {
  /** What kind of document this is — printed as the heading. */
  title: string;
  /** Its number, printed beside the heading and used as the page title. */
  documentNo: string;
  /** Head-of-document facts: date, party, warehouse, currency. */
  facts: PrintFact[];
  columns: PrintColumn[];
  /** Pre-formatted cells. Money is formatted by the caller — this module has no
   *  opinion about decimals and must not acquire one. */
  rows: string[][];
  totals?: PrintTotal[];
  narration?: string | null;
  /**
   * Lines under the table, before the signatures: which voucher posted it, what
   * it was returned against, whether it is cancelled.
   */
  notes?: string[];
  /** Left and right signature captions. Defaults to prepared / received. */
  signatures?: [string, string];
  /**
   * Stamped across the document when it no longer stands — a cancelled invoice
   * that somebody prints must not look like a live one.
   */
  watermark?: string | null;
}

/**
 * `@page { margin: 0 }` is what removes the browser's own header and footer.
 *
 * Chrome prints `8/18/26, 6:16 AM   INV-26-000002` across the top of every page
 * and `localhost:5173/sales/invoices   1/2` across the bottom, and there is no
 * CSS property that turns them off — but every engine suppresses them when the
 * page margin is zero, because there is nowhere left to put them. So the margin
 * moves off `@page` and onto the body as padding, which prints identically and
 * is not a place the browser writes into. (Somebody can still tick "Headers and
 * footers" in the print dialog; nothing in a web page can prevent that, and the
 * default is what matters.)
 *
 * Everything here is black on white and stays that way. A printed statement is
 * filed, faxed and photocopied, and a jade accent that reads as a mid grey on
 * the third copy is worse than no accent at all — so the only weights this uses
 * are rule thickness and font weight, which survive a photocopier.
 *
 * **Exported, and every composer in the application must use it.** The voucher
 * print kept its own `@page { margin: 16mm }` for a while, which is why a Cash
 * Payment came off the printer with `8/25/26, 6:36 AM   JV-26-000001` across the
 * head and `localhost:5173/finance/cp   1/1` across the foot while a sales
 * invoice came off clean. There is no second correct value here: a non-zero page
 * margin *is* the browser's header and footer.
 */
export const PAGE = `
  @page { size: A4; margin: 0; }
  html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { padding: 14mm 14mm 12mm; }
`;

const STYLE = `
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; position: relative; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #444; font-size: 11px; }
  h1 { font-size: 14px; margin: 14px 0 2px; text-transform: uppercase; letter-spacing: .08em; }
  .no { font-size: 12.5px; font-weight: 600; padding-bottom: 10px; }
  .facts { display: flex; flex-wrap: wrap; gap: 10px 28px; font-size: 11.5px; padding-bottom: 12px; }
  .facts b { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #999; padding: 5px 6px; vertical-align: top;
           text-align: left; font-size: 11.5px; }
  th { border-bottom: 1.2px solid #000; font-size: 10.5px; text-transform: uppercase;
       letter-spacing: .06em; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .totals { display: flex; justify-content: flex-end; padding-top: 10px; }
  .totals table { width: auto; min-width: 260px; }
  .totals td { border-bottom: 0; padding: 3px 6px; }
  .totals tr.strong td { border-top: 1.2px solid #000; font-weight: 600; padding-top: 5px; }
  .narration { padding-top: 12px; font-size: 11.5px; }
  .narration b { font-weight: 600; }
  .notes { padding-top: 12px; font-size: 11px; color: #333; }
  .sigs { display: flex; gap: 60px; padding-top: 56px; }
  .sig { flex: 1; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
  .foot { padding-top: 18px; font-size: 10px; color: #444; }
  .mark { position: fixed; top: 42%; left: 0; right: 0; text-align: center;
          font-size: 68px; font-weight: 700; letter-spacing: .12em; color: #000;
          opacity: .12; transform: rotate(-20deg); text-transform: uppercase; }
`;

/**
 * The letterhead, from the company registry. The one place it is written.
 *
 * **It is the company and nothing else.** Six fields, all of them maintained in
 * Setup → Company Profile, in the order somebody reads them:
 *
 *   logo · name · legal name · address, city, province · phone · email
 *
 * and then, on the right, the identifiers — **only the ones the profile says to
 * print**. `printIdentifiers()` resolves "does the company hold it" against
 * "should it appear", so a book that is not sales-tax registered does not print
 * an STRN it holds a number in. The legal name is dropped when it is the same
 * string as the name, because printing it twice says nothing.
 *
 * **The branch is not part of a letterhead and no longer appears here.** It used
 * to render as a fifth line under the identifiers, so a book kept in one place
 * printed the word `Gujranwala` under an address ending in Gujranwala. A branch
 * is a property of the *document* rather than of the company — the same reason
 * `BranchControl` renders only where there is more than one branch — and putting
 * it in the letterhead made it a claim about who we are. Nothing else was ever
 * passed it: of the two callers, one is the voucher form and one is the ledger's
 * voucher modal, and the other seven printed documents in the application never
 * carried it.
 *
 * **Exported for the voucher composer**, which had a hand-copied duplicate that
 * read `co.ntn` and `co.strn` directly — so the three "Print on documents" ticks
 * in Setup → Company Profile governed the invoice, the register and the trial
 * balance, and were silently ignored by the five voucher forms. That is the exact
 * failure `printIdentifiers()`'s own comment predicted for a fourth copy.
 */
export function letterhead(): string {
  const co = company();
  const location = companyLocation();
  const ids = printIdentifiers();
  return `
  <div class="head">
    <div style="display:flex; gap:10px; align-items:flex-start">
      ${co?.logoUrl ? `<img src="${esc(co.logoUrl)}" alt="" style="height:38px">` : ''}
      <div>
        <div class="co">${esc(co?.name ?? 'Company')}</div>
        ${co?.legalName && co.legalName !== co.name ? `<div class="muted">${esc(co.legalName)}</div>` : ''}
        ${location ? `<div class="muted">${esc(location)}</div>` : ''}
        ${co?.phone || co?.email ? `<div class="muted">${esc([co?.phone, co?.email].filter(Boolean).join(' · '))}</div>` : ''}
      </div>
    </div>
    <div class="muted" style="text-align:right">
      ${ids.map((i) => `<div>${esc(i.label)} ${esc(i.value)}</div>`).join('')}
    </div>
  </div>`;
}

function stamp(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}

/** Compose a trade document and send it to the printer. */
export function printDocument(doc: PrintDocument): void {
  const co = company();
  const align = (col: PrintColumn | undefined) => (col?.align === 'right' ? ' class="num"' : '');

  const head = doc.columns
    .map(
      (c) =>
        `<th${align(c)}${c.width ? ` style="width:${esc(c.width)}"` : ''}>${esc(c.label)}</th>`,
    )
    .join('');
  const body = doc.rows
    .map(
      (row) =>
        `<tr>${row.map((cell, i) => `<td${align(doc.columns[i])}>${esc(cell)}</td>`).join('')}</tr>`,
    )
    .join('');

  const totals = doc.totals?.length
    ? `<div class="totals"><table>${doc.totals
        .map(
          (t) =>
            `<tr${t.strong ? ' class="strong"' : ''}><td>${esc(t.label)}</td><td class="num">${esc(t.value)}</td></tr>`,
        )
        .join('')}</table></div>`
    : '';

  const [left, right] = doc.signatures ?? ['Prepared by', 'Received by'];

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(doc.documentNo || doc.title)}</title>
<style>${STYLE}</style></head>
<body>
  ${doc.watermark ? `<div class="mark">${esc(doc.watermark)}</div>` : ''}
  ${letterhead()}
  <h1>${esc(doc.title)}</h1>
  <div class="no">${esc(doc.documentNo)}</div>
  <div class="facts">${doc.facts
    .filter((f) => f.value)
    .map((f) => `<span><b>${esc(f.label)}</b> ${esc(f.value)}</span>`)
    .join('')}</div>

  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
  </table>
  ${totals}
  ${doc.narration ? `<div class="narration"><b>Narration</b> ${esc(doc.narration)}</div>` : ''}
  ${
    doc.notes?.length
      ? `<div class="notes">${doc.notes.map((n) => `<div>${esc(n)}</div>`).join('')}</div>`
      : ''
  }

  <div class="sigs">
    <div class="sig">&nbsp;<div class="muted">${esc(left)}</div></div>
    <div class="sig">&nbsp;<div class="muted">${esc(right)}</div></div>
  </div>

  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    Printed ${esc(stamp(new Date().toISOString()))}.
  </div>
</body></html>`;

  send(html);
}

// ── A report, rather than a screenshot of one ────────────────────────────────

/**
 * A row of a printed report.
 *
 * A plain array is an ordinary row. The object form carries the two weights a
 * financial report actually uses: `head` for a section band (`ASSETS`) and
 * `total` for a rule-above, bold line (`Total current assets`). Nothing else —
 * a report has no colours and no badges once it is on paper.
 */
export type ReportRow = string[] | { cells: string[]; head?: boolean; total?: boolean };

export interface ReportTable {
  /** The report's name, printed under the letterhead: `Chart of accounts`. */
  title: string;
  /**
   * The one line under the title that says what this particular run covers —
   * the date window, the account, the branch. Optional, and worth filling in:
   * a trial balance with no stated window is a figure nobody can reproduce.
   */
  meta?: string | null;
  columns: PrintColumn[];
  rows: ReportRow[];
  /** Under the table, above the footer: a caveat, a count, a basis of preparation. */
  notes?: string[];
  /** Used for the file name on export and the page title on print. */
  fileName?: string;
}

function reportRow(row: ReportRow): { cells: string[]; head: boolean; total: boolean } {
  return Array.isArray(row)
    ? { cells: row, head: false, total: false }
    : { cells: row.cells, head: row.head ?? false, total: row.total ?? false };
}

const REPORT_STYLE = `
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #333; font-size: 11px; }
  h1 { font-size: 14px; margin: 14px 0 2px; text-transform: uppercase; letter-spacing: .08em; }
  .meta { font-size: 11.5px; color: #333; padding-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 4px 6px; text-align: left; font-size: 11.5px; vertical-align: top;
           border-bottom: 1px solid #d0d0d0; }
  th { border-bottom: 1.2px solid #000; border-top: 1.2px solid #000;
       font-size: 10px; text-transform: uppercase; letter-spacing: .07em; font-weight: 600; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tr.head td { font-weight: 600; border-bottom: 1px solid #888; }
  tr.total td { font-weight: 600; border-top: 1px solid #000; border-bottom: 0; }
  .notes { padding-top: 12px; font-size: 11px; color: #333; }
  .foot { padding-top: 14px; margin-top: 14px; border-top: 1px solid #d0d0d0;
          font-size: 10px; color: #333; }
`;

/**
 * Print a report, rather than printing the screen it is on.
 *
 * Every report and register used to call `window.print()`, which prints the
 * application: the nav rail, the filter chips, the stat tiles totalling what the
 * chips had filtered to, the card borders, and — because a screen is a screen —
 * the browser's own date and URL stamped across the top and bottom. What belongs
 * in a filing cabinet is the letterhead, what the report is called, what it
 * covers, and the table. That is all this composes, and it composes it into a
 * hidden iframe so nothing on the page can leak into it by accident. The
 * separation is structural, not a rule somebody has to remember: a stat tile
 * cannot appear on the paper because the paper is built from `columns` and
 * `rows` and there is nowhere to put one.
 *
 * `printDocument()` above is the sibling for a *trade* document — an invoice has
 * a party, totals and two signatures, which a trial balance has none of. The two
 * share the letterhead and the page rules and nothing else, deliberately.
 */
export function printReport(report: ReportTable): void {
  const co = company();
  const align = (col: PrintColumn | undefined) => (col?.align === 'right' ? ' class="num"' : '');

  const head = report.columns
    .map(
      (c) =>
        `<th${align(c)}${c.width ? ` style="width:${esc(c.width)}"` : ''}>${esc(c.label)}</th>`,
    )
    .join('');

  const body = report.rows
    .map((raw) => {
      const row = reportRow(raw);
      const cls = row.total ? ' class="total"' : row.head ? ' class="head"' : '';
      const cells = report.columns
        .map((_, i) => `<td${align(report.columns[i])}>${esc(row.cells[i] ?? '')}</td>`)
        .join('');
      return `<tr${cls}>${cells}</tr>`;
    })
    .join('');

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(report.fileName ?? report.title)}</title>
<style>${REPORT_STYLE}</style></head>
<body>
  ${letterhead()}
  <h1>${esc(report.title)}</h1>
  ${report.meta ? `<div class="meta">${esc(report.meta)}</div>` : '<div class="meta"></div>'}

  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
  </table>
  ${
    report.notes?.length
      ? `<div class="notes">${report.notes.map((n) => `<div>${esc(n)}</div>`).join('')}</div>`
      : ''
  }
  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    Printed ${esc(stamp(new Date().toISOString()))}.
  </div>
</body></html>`;

  send(html);
}

/**
 * Hand composed HTML to the printer through a hidden iframe.
 *
 * Torn down on `afterprint` rather than immediately: the print dialog is modal
 * in some browsers and asynchronous in others, and removing the frame before it
 * has composed the page prints a blank sheet.
 *
 * Exported so the voucher composer prints through the same frame rather than its
 * own copy — the `load` / `readyState` pair below is the part a copy gets wrong,
 * and it is what keeps a logo from printing as a gap.
 */
export function send(html: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);

  const win = frame.contentWindow;
  if (!win) {
    frame.remove();
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.addEventListener('afterprint', () => frame.remove(), { once: true });

  // Once, whichever path gets there first. A logo is a network fetch and
  // printing before it lands leaves a gap where the letterhead should be — but
  // in a document with no images `load` has already fired by the time the
  // listener is attached, so both are needed and neither may fire twice.
  let sent = false;
  const print = () => {
    if (sent) return;
    sent = true;
    win.focus();
    win.print();
  };
  win.addEventListener('load', print);
  if (win.document.readyState === 'complete') print();
}
